import { GridCallbackDetails, GridFilterModel, GridPaginationModel, GridRowId, GridRowParams, GridSortModel, MuiEvent } from '@mui/x-data-grid-premium';
import React, { useEffect } from 'react';
import { useConfirmation } from 'src/base/ConfirmationDialog/ConfirmationService';
import { Button, DataGrid, IGridColDef } from 'src/components';
import { translate } from 'src/i18n/tHelper';
import { ApiRequest } from 'src/models/api.request';
import VwHastaislemRedpanikDTO from 'src/models/RedPanik/VwHastaislemRedpanikDTO';
import { ApiService } from 'src/services/core';
import { useAppDispatch, useAppSelector } from 'src/store';
import { RedPanikSlice } from 'src/store/Slices/RedPanik/RedPanikSlice';

const translatePath = "pages|redPanik";
const RedPanikIslemList = (): JSX.Element => {
    const state = useAppSelector(s => s.redPanik);
    const dispatch = useAppDispatch();
    const confirm = useConfirmation();

    const query = async (req: ApiRequest<VwHastaislemRedpanikDTO>) => {
        var service = new ApiService<VwHastaislemRedpanikDTO>("RedPanik");
        var res = await service.post("RedPanikHastaIslemListesi", req);
        if (res.responseCode !== 200) {
            confirm({
                description: res.errorMessage,
                variant: "info",
            });
            dispatch(RedPanikSlice.actions.setIslemListData({
                ...res,
                isFetching: false,
                isLoading: false,
            }));
        }
        dispatch(RedPanikSlice.actions.setIslemListData({
            ...res,
            isFetching: false,
            isLoading: false,
        }));
    };

    useEffect(() => {
        (async () => {
            if (!state.islemListData.isLoading) return;
            if (!state.hastaListActiveRow?.id) return;
            dispatch(RedPanikSlice.actions.setIslemListData({
                ...state.islemListData,
                isFetching: true,
            }));
            let req: ApiRequest<VwHastaislemRedpanikDTO> = {
                paging: true,
                pageIndex: state.islemListPaginationModel.page,
                pageSize: state.islemListPaginationModel.pageSize,
                filters: { groupOperator: "and", predicates: [] },
                sorts: []
            };
            if (state.hastaListActiveRow?.id > 0)
                req.filters.predicates.push({
                    fieldName: "hastakimlikId",
                    operator: "=",
                    value1: state.hastaListActiveRow.id
                });
            if (state.islemListFilterModel && state.islemListFilterModel.items?.length > 0) {
                state.islemListFilterModel.items.forEach(filterItem => {
                    const operator = filterItem.operator === "contains" ? "like" :
                        filterItem.operator === "equals" ? "=" :
                            filterItem.operator === "startsWith" ? "startsWith" :
                                filterItem.operator === "endsWith" ? "endsWith" :
                                    filterItem.operator === "isEmpty" ? "empty" :
                                        filterItem.operator === "isNotEmpty" ? "empty" :
                                            "like";

                    req.filters.predicates.push({
                        fieldName: filterItem.field as keyof VwHastaislemRedpanikDTO,
                        operator: operator as "endsWith" | "startsWith" | "=" | ">" | ">=" | "<" | "<=" | "like" | "empty" | "in" | "between",
                        value1: filterItem.value,
                        value2: null,
                        not: filterItem.operator === "isNotEmpty" ? true : false,
                    });
                });
            }
            if (state.islemListSortModel && state.islemListSortModel?.length > 0) {
                state.islemListSortModel.forEach(sortItem => {
                    req.sorts.push({
                        fieldName: sortItem.field as keyof VwHastaislemRedpanikDTO,
                        ascending: sortItem.sort === 'asc'
                    });
                });
            } else {
                req.sorts.push({ fieldName: "islemId", ascending: false });
            }
            await query(req);
        })();
    }, [state.islemListPaginationModel, state.islemListSortModel, state.islemListFilterModel, state.islemListData.isLoading]);

    useEffect(() => {
        if (state.islemListData?.data?.length > 0 && !state.islemListData.isFetching)
            if (!state.islemListActiveRow?.islemId)
                dispatch(RedPanikSlice.actions.setIslemListActiveRow(state.islemListData.data?.at(0)));
    }, [state.islemListData.isFetching]);

    useEffect(() => {
        if (!state.hastaListActiveRow?.id) return;
        if (state.hastaListActiveRow?.id <= 0) return;
        return () => {
            dispatch(RedPanikSlice.actions.setIslemListData({
                ...state.islemListData,
                isLoading: true,
                isFetching: true,
            }));
        }
    }, []);

    useEffect(() => {
        if (!state.hastaListActiveRow?.id) return;
        if (state.hastaListActiveRow?.id <= 0) return;
        handlerSearch(null);
    }, [state.hastaListActiveRow?.id]);

    const handlerSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        dispatch(RedPanikSlice.actions.setIslemListActiveRow(new VwHastaislemRedpanikDTO()));
        dispatch(RedPanikSlice.actions.setIslemListData({
            ...state.islemListData,
            isLoading: true,
            isFetching: true,
        }));
    }

    const onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails): void => {
        dispatch(RedPanikSlice.actions.setIslemListPaginationModel(model));
    }
    const onSortModelChange = (model: GridSortModel, details: GridCallbackDetails): void => {
        dispatch(RedPanikSlice.actions.setIslemListSortModel(model));
    }
    const onFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<"filter">): void => {
        dispatch(RedPanikSlice.actions.setIslemListFilterModel(model));
    }
    const getRowId = (row: VwHastaislemRedpanikDTO): GridRowId => { return row.islemId; };

    const filterData = (data: VwHastaislemRedpanikDTO[]): VwHastaislemRedpanikDTO[] => {
        if (!Array.isArray(data)) return [];
        return data;
    }

    const onRowClick = (params: GridRowParams<VwHastaislemRedpanikDTO>, event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>, details: GridCallbackDetails): void => {
        dispatch(RedPanikSlice.actions.setIslemListActiveRow(params.row));
    }

    const columns: IGridColDef<VwHastaislemRedpanikDTO>[] = [
        {
            field: "redtarih",
            headerName: translate(translatePath, "Red Tarihi"),
            width: 140,
            type: "dateTime",
        },
        {
            field: "islemkodu",
            headerName: translate(translatePath, "İşlem Kodu"),
            width: 110,
        },
        {
            field: "islemadi",
            headerName: translate(translatePath, "İşlem Adı"),
            flex: 1,
            minWidth: 300
        },
        {
            field: "kurumAdi",
            headerName: translate(translatePath, "Kurum Adı"),
            flex: 1,
            minWidth: 150
        },
        {
            field: "organizasyonAdi",
            headerName: translate(translatePath, "Organizasyon Adı"),
            flex: 1,
            minWidth: 100
        },
    ];

    return (
        <DataGrid
            unstable_headerFilters
            hiddenColumnsButton
            hideFooterSelectedRowCount
            hiddenExport
            hiddenFilters
            sx={{ height: "60vh" }}
            rows={filterData(state.islemListData?.data) || []}
            columns={columns}
            loading={state.islemListData.isFetching}
            rowCount={state.islemListData?.totalRecords || 0}
            rowSelectionModel={state.islemListActiveRow ? [state.islemListActiveRow.islemId] : []}
            onRowClick={onRowClick}
            pagination={true}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            paginationModel={state.islemListPaginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={state.islemListSortModel}
            onSortModelChange={onSortModelChange}
            filterModel={state.islemListFilterModel}
            onFilterModelChange={onFilterModelChange}
            getRowId={getRowId}
            slots={{
                headerFilterMenu: null
            }}
        />
    );
};

export default RedPanikIslemList;